import React, { useState, useEffect, useContext, useRef } from "react";
import DataTable from "react-data-table-component";
import { toast } from "react-toastify";
import { FiDownload, FiEye, FiX } from "react-icons/fi";
import AuthContext from "../../contexts/AuthContext";
import AdminLayout from "../../layouts";
import "./new-contact-enquiries.css";

// Options for the "Services" dropdown filter.
const SERVICE_OPTIONS = [
  "SEO Optimization",
  "Social Media Marketing",
  "Influencer Marketing",
  "PR Management",
  "Branding",
  "Website Development",
  "Google Ads",
  "Production House",
  "Augmented Reality",
  "AI Automation",
  "Performance Marketing",
  "Trademark Registration",
];

const EMPTY_FILTERS = {
  firstName: "",
  lastName: "",
  phoneNo: "",
  email: "",
  companyName: "",
  sector: "",
  service: "",
  fromDate: "",
  toDate: "",
};

const NewContactEnquiries = () => {
  const { token, triggerSessionExpired } = useContext(AuthContext);

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [exporting, setExporting] = useState(false);

  // Temporary state for the input fields.
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // Active state that actually triggers the fetch.
  const [activeSearch, setActiveSearch] = useState({});

  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  // Tracks the in-flight request so a fast page/filter change can cancel
  // a stale one instead of letting it overwrite newer results.
  const abortControllerRef = useRef(null);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, activeSearch]);

  useEffect(() => {
    return () => abortControllerRef.current?.abort();
  }, []);

  // Lets the detail modal be dismissed with Escape, matching standard modal UX.
  useEffect(() => {
    if (!selectedEnquiry) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setSelectedEnquiry(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedEnquiry]);

  const fetchData = async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/newContactEnquiry/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            pkNewContactEnqId: "",
            fName: activeSearch.firstName || "",
            lName: activeSearch.lastName || "",
            phoneNo: activeSearch.phoneNo || "",
            userEmail: activeSearch.email || "",
            companyName: activeSearch.companyName || "",
            sector: activeSearch.sector || "",
            services: activeSearch.service || "",
            fromDate: activeSearch.fromDate || "",
            toDate: activeSearch.toDate || "",
            pageSize: String(pageSize),
            pageNo: String(page),
          }),
          signal: controller.signal,
        }
      );

      if (response.status === 401 || response.status === 403) {
        triggerSessionExpired();
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || `Request failed (${response.status})`);
      }

      // Backend returns "Successful" when records exist and "success" when
      // the result set is empty — normalize the check instead of trusting casing.
      const status = (data?.status || "").toLowerCase();
      if (status && status !== "successful" && status !== "success") {
        toast.error(data?.message || "Failed to fetch new contact enquiries.");
        setContacts([]);
        setTotalRows(0);
        return;
      }

      setContacts(Array.isArray(data?.data) ? data.data : []);
      setTotalRows(data?.totalResult || 0);
    } catch (error) {
      if (error.name === "AbortError") return; // superseded by a newer request
      console.error("Error fetching new contact enquiries:", error);
      toast.error(error.message || "Failed to fetch new contact enquiries.");
      setContacts([]);
      setTotalRows(0);
    } finally {
      // Only the most recent request should be allowed to clear the spinner.
      if (abortControllerRef.current === controller) {
        setLoading(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearch = () => {
    setPage(1); // Reset to first page on new search
    setActiveSearch(filters);
  };

  const handleClear = () => {
    setFilters(EMPTY_FILTERS);
    setActiveSearch(EMPTY_FILTERS);
    setPage(1);
  };

  // Exports ALL rows matching the current filters, not just the loaded page —
  // fetches a fresh copy with pageSize = totalRows so the CSV matches what the
  // search/filter says is there, not just what's rendered in the table.
  const handleExport = async () => {
    if (!totalRows) {
      toast.info("No data to export.");
      return;
    }

    setExporting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/newContactEnquiry/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            pkNewContactEnqId: "",
            fName: activeSearch.firstName || "",
            lName: activeSearch.lastName || "",
            phoneNo: activeSearch.phoneNo || "",
            userEmail: activeSearch.email || "",
            companyName: activeSearch.companyName || "",
            sector: activeSearch.sector || "",
            services: activeSearch.service || "",
            fromDate: activeSearch.fromDate || "",
            toDate: activeSearch.toDate || "",
            pageSize: String(totalRows),
            pageNo: "1",
          }),
        }
      );

      if (response.status === 401 || response.status === 403) {
        triggerSessionExpired();
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || `Request failed (${response.status})`);
      }

      const allRows = Array.isArray(data?.data) ? data.data : [];

      if (!allRows.length) {
        toast.info("No data to export.");
        return;
      }

      const headers = [
        "First Name",
        "Last Name",
        "Phone No",
        "Email",
        "Company Name",
        "Sector",
        "Services",
        "Date",
      ];

      const escapeCell = (val) => {
        const str = val === null || val === undefined ? "" : String(val);
        // Wrap in quotes and escape any embedded quotes for CSV safety.
        return `"${str.replace(/"/g, '""')}"`;
      };

      const rows = allRows.map((row) =>
        [
          row?.fname ?? row?.firstName,
          row?.lname ?? row?.lastName,
          row?.userPhoneNo ?? row?.phoneNo,
          row?.userEmail ?? row?.email,
          row?.companyName,
          row?.sector,
          row?.services,
          row?.createdDate,
        ]
          .map(escapeCell)
          .join(",")
      );

      const csvContent = [headers.map(escapeCell).join(","), ...rows].join("\r\n");

      const blob = new Blob(["﻿" + csvContent], {
        type: "text/csv;charset=utf-8;",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `new-contact-enquiries-${new Date().toISOString().slice(0, 10)}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("Error exporting new contact enquiries:", error);
      toast.error(error.message || "Failed to export data. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const columns = [
    {
      name: "First Name",
      selector: (row) => row?.fname || row?.firstName || "-",
      sortable: true,
    },
    {
      name: "Last Name",
      selector: (row) => row?.lname || row?.lastName || "-",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row?.userPhoneNo || row?.phoneNo || "-",
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row?.userEmail || row?.email || "-",
      sortable: true,
    },
    {
      name: "Company",
      selector: (row) => row?.companyName || "-",
      sortable: true,
    },
    {
      name: "Sector",
      selector: (row) => row?.sector || "-",
      sortable: true,
    },
    {
      name: "Services",
      cell: (row) => (
        <div className="order-id-cell" title={row?.services}>
          {row?.services || "-"}
        </div>
      ),
    },
    {
      name: "Date",
      selector: (row) => row?.createdDate || "-",
      sortable: true,
    },
    {
      name: "Action",
      cell: (row) => (
        <button
          type="button"
          className="action-view-btn"
          onClick={() => setSelectedEnquiry(row)}
          aria-label="View details"
        >
          <FiEye size={16} />
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "90px",
    },
  ];

  // Themes the table to match the panel's premium redesign (brand-gradient
  // header row, softer hover tint) without touching react-data-table-component's markup.
  const tableCustomStyles = {
    headRow: {
      style: {
        background: "var(--admin-bg)",
        minHeight: "52px",
        borderBottom: "1px solid var(--admin-border)",
      },
    },
    headCells: {
      style: {
        color: "var(--admin-text-primary)",
        fontSize: "13px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.03em",
      },
    },
    rows: {
      style: {
        minHeight: "54px",
        fontSize: "14px",
        color: "#29282b",
      },
      highlightOnHoverStyle: {
        backgroundColor: "rgba(0, 180, 255, 0.08)",
        borderBottomColor: "#e4e7ee",
        outline: "none",
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #eef0f5",
      },
    },
  };

  return (
    <AdminLayout>
      <div className="new-contact-enquiries">
        <div className="page-banner">
          <div className="page-banner-text">
            <span className="page-banner-tag">Enquiries</span>
            <h1 className="page-banner-title">New Contact Enquiries</h1>
          </div>
          <div className="page-banner-badge">
            <span className="page-banner-badge-label">Total</span>
            <span className="page-banner-badge-value">{totalRows}</span>
          </div>
        </div>

        {/* --- SEARCH FILTER SECTION --- */}
        <div className="search-filter-card">
          <div className="search-grid">
            <div className="filter-group">
              <label>First Name:</label>
              <input
                type="text"
                name="firstName"
                placeholder="Enter First Name"
                value={filters.firstName}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Last Name:</label>
              <input
                type="text"
                name="lastName"
                placeholder="Enter Last Name"
                value={filters.lastName}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Phone No:</label>
              <input
                type="text"
                name="phoneNo"
                placeholder="Enter Phone No"
                value={filters.phoneNo}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Email ID:</label>
              <input
                type="email"
                name="email"
                placeholder="Enter Email"
                value={filters.email}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Company Name:</label>
              <input
                type="text"
                name="companyName"
                placeholder="Enter Company Name"
                value={filters.companyName}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Sector:</label>
              <input
                type="text"
                name="sector"
                placeholder="Enter Sector"
                value={filters.sector}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>Services:</label>
              <select
                name="service"
                value={filters.service}
                onChange={handleInputChange}
              >
                <option value="">All Services</option>
                {SERVICE_OPTIONS.map((service) => (
                  <option key={service} value={service}>
                    {service}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>From Date:</label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleInputChange}
              />
            </div>
            <div className="filter-group">
              <label>To Date:</label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="search-actions">
            <button className="btn-search" onClick={handleSearch}>
              Search
            </button>
            <button className="btn-clear" onClick={handleClear}>
              Clear
            </button>
            <button className="btn-export" onClick={handleExport} disabled={exporting}>
              <FiDownload size={16} />
              {exporting ? "Exporting..." : "Export"}
            </button>
          </div>
        </div>

        {/* --- DATA TABLE SECTION --- */}
        <div className="new-contact-enquiries-body">
          <DataTable
            keyField="pkNewContactEnq"
            columns={columns}
            data={contacts}
            progressPending={loading}
            pagination
            paginationServer
            paginationTotalRows={totalRows}
            paginationPerPage={pageSize}
            onChangePage={(p) => setPage(p)}
            onChangeRowsPerPage={(ps, p) => {
              setPageSize(ps);
              setPage(p);
            }}
            highlightOnHover
            customStyles={tableCustomStyles}
          />
        </div>
      </div>

      {selectedEnquiry && (
        <div
          className="detail-modal-overlay"
          onClick={() => setSelectedEnquiry(null)}
        >
          <div className="detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h3>New Contact Enquiry Details</h3>
              <button
                type="button"
                className="detail-modal-close"
                onClick={() => setSelectedEnquiry(null)}
                aria-label="Close"
              >
                <FiX size={20} />
              </button>
            </div>
            <div className="detail-modal-body">
              <div className="detail-row">
                <span className="detail-label">First Name</span>
                <span className="detail-value">
                  {selectedEnquiry?.fname || selectedEnquiry?.firstName || "-"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Last Name</span>
                <span className="detail-value">
                  {selectedEnquiry?.lname || selectedEnquiry?.lastName || "-"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Phone</span>
                <span className="detail-value">
                  {selectedEnquiry?.userPhoneNo || selectedEnquiry?.phoneNo || "-"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Email</span>
                <span className="detail-value">
                  {selectedEnquiry?.userEmail || selectedEnquiry?.email || "-"}
                </span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Company</span>
                <span className="detail-value">{selectedEnquiry?.companyName || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Sector</span>
                <span className="detail-value">{selectedEnquiry?.sector || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Services</span>
                <span className="detail-value">{selectedEnquiry?.services || "-"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Date</span>
                <span className="detail-value">{selectedEnquiry?.createdDate || "-"}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default NewContactEnquiries;
